import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { type FC } from 'react';

export interface SetupStep {
  sectionId: string;
  label: string;
  isComplete: boolean;
  isOptional?: boolean;
}

interface SetupChecklistProps {
  patientFirstName: string;
  steps: SetupStep[];
  onSelectSection: (sectionId: string) => void;
}

const SetupChecklist: FC<SetupChecklistProps> = ({ patientFirstName, steps, onSelectSection }) => {
  // Optional steps (AI monitoring) are listed but never keep the banner alive, otherwise
  // patients who deliberately run without them would be nagged forever.
  const nextStep = steps.find((step) => !step.isComplete && !step.isOptional);
  if (!nextStep) {
    return null;
  }

  const requiredSteps = steps.filter((step) => !step.isOptional);
  const completedCount = requiredSteps.filter((step) => step.isComplete).length;

  return (
    <Paper
      variant={'outlined'}
      sx={{
        p: 2.5,
        mt: 3,
        borderColor: 'info.main',
        backgroundColor: (theme) =>
          theme.palette.mode === 'light' ? 'rgba(2,136,209,0.05)' : 'rgba(41,182,246,0.08)',
      }}
    >
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        <Stack>
          <Typography variant={'h5'} component={'h2'}>
            Finish setting up {patientFirstName}&apos;s profile
          </Typography>
          <Typography variant={'body2'} color={'text.secondary'}>
            {completedCount} of {requiredSteps.length} sections done. Monitoring starts once a schedule exists.
          </Typography>
        </Stack>
        <Button
          variant={'contained'}
          sx={{ ml: 'auto', flexShrink: 0 }}
          onClick={() => onSelectSection(nextStep.sectionId)}
        >
          Set up {nextStep.label.toLowerCase()}
        </Button>
      </Stack>
      <Stack direction={'row'} spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {steps.map((step, index) => (
          <Chip
            key={step.sectionId}
            size={'small'}
            clickable
            onClick={() => onSelectSection(step.sectionId)}
            icon={step.isComplete ? <CheckCircleIcon /> : undefined}
            color={step.isComplete ? 'success' : 'default'}
            variant={step.isComplete || step.sectionId === nextStep.sectionId ? 'filled' : 'outlined'}
            label={step.isComplete ? step.label : `${index + 1} · ${step.label}`}
          />
        ))}
      </Stack>
    </Paper>
  );
};

export default SetupChecklist;
